import { useEffect, useRef } from 'react';
import { applyListChange } from '../helpers/medicalCheckupListPatch';
import {
    HistoryMedicalCheckupsListFilter,
    matchesHistoryListFilter,
    matchesNewListFilter,
    NewMedicalCheckupsListFilter,
} from '../helpers/medicalCheckupListFilter';
import { isCurrentTenantCheckup } from '../helpers/medicalCheckupTenantGuard';
import { SignalrEvents } from '../models/enums/signalrEvents';
import { MedicalCheckupListChangedEvent } from '../models/medicalCheckups/medicalCheckupListChangedEvent';
import { MedicalCheckupShortDto } from '../models/medicalCheckups/medicalCheckupShortDto';
import SignalrService from '../services/signalrService';

type ListType = 'new' | 'history';

interface UseMedicalCheckupListSyncParams {
    listType: ListType;
    filter: NewMedicalCheckupsListFilter | HistoryMedicalCheckupsListFilter;
    page: number;
    rowsPerPage: number;
    onPatch: (items: MedicalCheckupShortDto[], count: number) => void;
    onRefetch: () => void;
    getCurrentState: () => { items: MedicalCheckupShortDto[]; count: number };
}

export function useMedicalCheckupListSync({
    listType,
    filter,
    page,
    rowsPerPage,
    onPatch,
    onRefetch,
    getCurrentState,
}: UseMedicalCheckupListSyncParams): void {
    const onPatchRef = useRef(onPatch);
    const onRefetchRef = useRef(onRefetch);
    const getCurrentStateRef = useRef(getCurrentState);
    const filterRef = useRef(filter);
    const pageRef = useRef(page);
    const rowsPerPageRef = useRef(rowsPerPage);
    const listTypeRef = useRef(listType);

    onPatchRef.current = onPatch;
    onRefetchRef.current = onRefetch;
    getCurrentStateRef.current = getCurrentState;
    filterRef.current = filter;
    pageRef.current = page;
    rowsPerPageRef.current = rowsPerPage;
    listTypeRef.current = listType;

    useEffect(() => {
        const { subscribeEvent, unsubscribeEvent, onReconnected, offReconnected } = SignalrService();

        const matches = (checkup: MedicalCheckupShortDto) => {
            if (listTypeRef.current === 'new') {
                return matchesNewListFilter(checkup, filterRef.current as NewMedicalCheckupsListFilter);
            }
            return matchesHistoryListFilter(checkup, filterRef.current as HistoryMedicalCheckupsListFilter);
        };

        const handleEvent = (event: MedicalCheckupListChangedEvent) => {
            if (!isCurrentTenantCheckup(event.checkup)) {
                return;
            }

            const { items, count } = getCurrentStateRef.current();
            const result = applyListChange(items, count, event, {
                page: pageRef.current,
                rowsPerPage: rowsPerPageRef.current,
                matches,
            });
            onPatchRef.current(result.items, result.count);
        };

        const handleReconnected = () => {
            onRefetchRef.current();
        };

        const handleOnline = () => {
            onRefetchRef.current();
        };

        subscribeEvent(SignalrEvents.MedicalCheckupListChanged, handleEvent);
        onReconnected(handleReconnected);
        window.addEventListener('online', handleOnline);

        return () => {
            unsubscribeEvent(SignalrEvents.MedicalCheckupListChanged, handleEvent);
            offReconnected(handleReconnected);
            window.removeEventListener('online', handleOnline);
        };
    }, []);
}
